// assets/src/js/features/product/components/productsArchive/FilterSidebar.tsx

import React, { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import {
  buildCategoryTree,
  hasCategoryProducts,
  CategoryWithChildren,
} from "../../helpers/productsCategoryHelpers";
import type { FilterState } from "../../productSlice";
import type {
  TransformedWPCategory,
  TransformedWPTag,
  TransformedWPBrand,
} from "../../types/productTaxonomyApiTypes";

// Define props interface
interface FilterSidebarProps {
  categories: TransformedWPCategory[];
  tags: TransformedWPTag[]; // Still receives all tags initially
  brands: TransformedWPBrand[];
  activeFilters: FilterState;
  onCategoryClick: (categoryId: number | null) => void;
  onTagClick: (tagId: number) => void;
  onBrandClick: (brandSlug: string) => void;
  onPriceFilter: ({
    minPrice,
    maxPrice,
  }: {
    minPrice: number | null;
    maxPrice: number | null;
  }) => void;
  onSearchFilter: (searchQuery: string) => void;
  onSaleToggle: (checked: boolean) => void;
  isLoading?: boolean; // Loading state for all filter data
  onClose?: () => void; // Callback to close the offcanvas (for mobile)
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  categories,
  tags,
  brands,
  activeFilters,
  onCategoryClick,
  onTagClick,
  onBrandClick,
  onPriceFilter,
  onSearchFilter,
  onSaleToggle,
  isLoading = false,
  onClose,
}) => {
  const { t } = useTranslation();

  // State for tracking expanded category sections
  const [expandedCategories, setExpandedCategories] = useState<
    Record<number, boolean>
  >({});
  // State for search input
  const [searchValue, setSearchValue] = useState<string>(
    activeFilters.search || ""
  );

  // Update search value when activeFilters.search changes (e.g., from URL)
  useEffect(() => {
    setSearchValue(activeFilters.search || "");
  }, [activeFilters.search]);

  // Build hierarchical category structure using helper
  const categoryTree = useMemo(
    () => buildCategoryTree(categories),
    [categories]
  );

  // Calculate the top 8 most used tags
  const topTags = useMemo(() => {
    if (!tags || !Array.isArray(tags)) {
      return [];
    }
    // Sort tags by count descending and take top 8
    return [...tags] // Create a copy before sorting
      .sort((a, b) => b.count - a.count) // Sort descending by count
      .slice(0, 8); // Take the first 8 elements
  }, [tags]); // Recompute only when the 'tags' prop changes

  // Toggle category expansion
  const toggleCategory = (categoryId: number): void => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryId]: !prev[categoryId],
    }));
  };

  // Check if a category should be shown as active
  const isCategoryActive = (id: number): boolean => {
    return activeFilters.category.includes(id);
  };

  // Check if any child category is active (for expanding parent)
  const hasActiveChild = (category: CategoryWithChildren): boolean => {
    if (isCategoryActive(category.id)) return true;
    if (category.children && category.children.length > 0) {
      return category.children.some((child) => hasActiveChild(child));
    }
    return false;
  };

  // --- SKELETON RENDERING FUNCTIONS ---
  const renderCategorySkeletons = (count = 5): React.ReactNode => {
    return [
      <li key="all-products-skeleton" className="category-skeleton">
        <Skeleton height={24} width="40%" />
      </li>,
      ...Array(count)
        .fill(0)
        .map((_, index) => (
          <li key={`category-skeleton-${index}`} className="category-skeleton">
            <div className="category-item">
              <Skeleton height={24} width={index % 2 === 0 ? "75%" : "60%"} />
            </div>
            {index % 3 === 0 && (
              <ul className="subcategories">
                <li className="category-skeleton">
                  <Skeleton height={20} width="50%" />
                </li>
                <li className="category-skeleton">
                  <Skeleton height={20} width="60%" />
                </li>
              </ul>
            )}
          </li>
        )),
    ];
  };

  const renderTagSkeletons = (count = 8): React.ReactNode => {
    return Array(count)
      .fill(0)
      .map((_, index) => (
        <Skeleton
          key={`tag-skeleton-${index}`}
          height={28}
          width={60}
          style={{
            marginRight: "8px",
            marginBottom: "8px",
            borderRadius: "3px",
          }}
        />
      ));
  };

  const renderBrandSkeletons = (count = 4): React.ReactNode => {
    return Array(count)
      .fill(0)
      .map((_, index) => (
        <Skeleton
          key={`brand-skeleton-${index}`}
          height={28}
          width={80}
          style={{
            marginRight: "8px",
            marginBottom: "8px",
            borderRadius: "3px",
          }}
        />
      ));
  };

  const renderPriceRangeSkeletons = (): React.ReactNode => {
    return (
      <div className="price-range-form skeleton-price-range">
        <Skeleton height={30} width={150} />
      </div>
    );
  };

  const renderOnSaleCheckboxSkeleton = (): React.ReactNode => {
    return (
      <div className="on-sale-skeleton">
        <Skeleton height={24} width="80%" style={{ marginBottom: "8px" }} />
      </div>
    );
  };

  const renderSearchSkeleton = (): React.ReactNode => {
    return (
      <div className="search-skeleton">
        <Skeleton height={36} width="100%" style={{ marginBottom: "8px" }} />
      </div>
    );
  };
  // --- END SKELETONS ---

  // --- CATEGORY RENDER FUNCTION ---
  const renderCategory = (category: CategoryWithChildren): React.ReactNode => {
    if (!hasCategoryProducts(category)) {
      return null;
    }
    const isExpanded =
      expandedCategories[category.id] || hasActiveChild(category);
    const validChildren = category.children
      ? category.children.filter(hasCategoryProducts)
      : [];
    const hasChildren = validChildren.length > 0;
    const isActive = isCategoryActive(category.id);
    return (
      <li key={category.id} className={hasChildren ? "has-children" : ""}>
        <div className="category-item">
          <div className="category-checkbox-wrapper">
            <input
              type="checkbox"
              id={`category-${category.id}`}
              checked={isActive}
              onChange={(e) => {
                e.preventDefault();
                onCategoryClick(category.id);
              }}
            />
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                onCategoryClick(category.id);
              }}
              className={`category-link ${isActive ? "active" : ""}`}
              data-category={category.id}
            >
              {category.name}
              <span className="count">({category.count})</span>
            </a>
          </div>
          {hasChildren && (
            <button
              className={`toggle-subcategories ${isExpanded ? "expanded" : ""}`}
              onClick={(e) => {
                e.preventDefault();
                toggleCategory(category.id);
              }}
              aria-expanded={isExpanded}
              aria-label={
                isExpanded
                  ? t("filters.collapseSubcategories")
                  : t("filters.expandSubcategories")
              }
            >
              <span className="sr-only">
                {isExpanded ? t("filters.collapse") : t("filters.expand")}{" "}
              </span>
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d={isExpanded ? "M7 11l5-5 5 5H7z" : "M7 10l5 5 5-5H7z"}
                />
              </svg>
            </button>
          )}
        </div>
        {hasChildren && isExpanded && (
          <ul className="subcategories">
            {validChildren.map((child) => renderCategory(child))}
          </ul>
        )}
      </li>
    );
  };
  // --- END CATEGORY RENDER ---

  // --- FORM HANDLERS ---
  const handlePriceSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const formElements = e.currentTarget.elements as HTMLFormControlsCollection;
    const minPriceInput = formElements.namedItem(
      "min_price"
    ) as HTMLInputElement;
    const maxPriceInput = formElements.namedItem(
      "max_price"
    ) as HTMLInputElement;
    const minPrice = parseFloat(minPriceInput.value) || null;
    const maxPrice = parseFloat(maxPriceInput.value) || null;
    if (minPrice !== null || maxPrice !== null) {
      onPriceFilter({ minPrice, maxPrice });
    }
  };

  const handleClearPriceFilter = (
    e: React.MouseEvent<HTMLButtonElement>
  ): void => {
    e.preventDefault();
    onPriceFilter({ minPrice: null, maxPrice: null });
    const form = e.currentTarget.closest("form") as HTMLFormElement;
    if (form) {
      const minPriceInput = form.elements.namedItem(
        "min_price"
      ) as HTMLInputElement;
      const maxPriceInput = form.elements.namedItem(
        "max_price"
      ) as HTMLInputElement;
      minPriceInput.value = "";
      maxPriceInput.value = "";
    }
  };

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    onSearchFilter(searchValue);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchValue(e.target.value);
  };

  const handleClearSearch = (): void => {
    setSearchValue("");
    onSearchFilter("");
  };
  // --- END FORM HANDLERS ---

  return (
    <aside className="product-filters-offcanvas">
      {/* Offcanvas Header */}
      <div className="filter-offcanvas-header">
        <h3 id="filter-offcanvas-title">{t("filters.title", "Filters")}</h3>
        {onClose && (
          <button
            className="filter-close-button"
            onClick={onClose}
            aria-label={t("common.close", "Close")}
          >
            ×
          </button>
        )}
      </div>

      {/* Scrollable Content Area */}
      <div className="filter-offcanvas-content">
        {/* Brands section */}
        <div className="filter-section">
          {isLoading ? (
            <Skeleton
              width={100}
              height={20}
              className="filter-section-header-skeleton"
            />
          ) : (
            <h3>{t("filters.brands")}</h3>
          )}
          <div className="product-brands">
            {isLoading ? (
              renderBrandSkeletons()
            ) : brands && brands.length > 0 ? (
              brands
                .filter((b) => b.count > 0)
                .map((brand) => (
                  <a
                    key={brand.id}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      onBrandClick(brand.slug);
                    }}
                    className={`brand-link ${
                      activeFilters.brand.includes(brand.slug) ? "active" : ""
                    }`}
                    data-brand={brand.slug}
                  >
                    {brand.name}
                    <span className="count">({brand.count})</span>
                  </a>
                ))
            ) : (
              <div className="no-brands-message">{t("filters.noBrands")}</div>
            )}
          </div>
        </div>

        {/* Categories section */}
        <div className="filter-section">
          {isLoading ? (
            <Skeleton
              width={120}
              height={20}
              className="filter-section-header-skeleton"
            />
          ) : (
            <h3>{t("filters.categories")}</h3>
          )}
          <ul className="product-categories hierarchical">
            {isLoading ? (
              renderCategorySkeletons()
            ) : (
              <>
                <li key="all-products">
                  <div className="category-item">
                    <div className="category-checkbox-wrapper">
                      <input
                        type="checkbox"
                        id="category-all"
                        checked={activeFilters.category.length === 0}
                        onChange={(e) => {
                          e.preventDefault();
                          if (activeFilters.category.length > 0) {
                            onCategoryClick(null);
                          }
                        }}
                      />
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (activeFilters.category.length > 0) {
                            onCategoryClick(null);
                          }
                        }}
                        className={`category-link ${
                          activeFilters.category.length === 0 ? "active" : ""
                        }`}
                        data-category="all"
                      >
                        {t("filters.allProducts")}
                      </a>
                    </div>
                  </div>
                </li>
                {categoryTree && categoryTree.length > 0 ? (
                  categoryTree.filter(hasCategoryProducts).map(renderCategory)
                ) : (
                  <li>{t("filters.noCategories")}</li>
                )}
              </>
            )}
          </ul>
        </div>

        {/* Tags section */}
        <div className="filter-section">
          {isLoading ? (
            <Skeleton
              width={80}
              height={20}
              className="filter-section-header-skeleton"
            />
          ) : (
            <h3>{t("filters.tags")}</h3>
          )}
          <div className="product-tags">
            {isLoading ? (
              renderTagSkeletons()
            ) : topTags && topTags.length > 0 ? (
              topTags.map((tag) => (
                <a
                  key={tag.id}
                  href="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onTagClick(tag.id);
                  }}
                  className={`tag-link ${
                    activeFilters.tag.includes(tag.id) ? "active" : ""
                  }`}
                  data-tag={tag.id}
                >
                  {tag.name}
                  {/* <span className="count">({tag.count})</span> */}
                </a>
              ))
            ) : (
              <div className="no-tags-message">{t("filters.noTags")}</div>
            )}
          </div>
        </div>

        {/* Price Range Section */}
        <div className="filter-section">
          {isLoading ? (
            <Skeleton
              width={110}
              height={20}
              className="filter-section-header-skeleton"
            />
          ) : (
            <h3>{t("filters.priceRange")}</h3>
          )}
          {isLoading ? (
            renderPriceRangeSkeletons()
          ) : (
            <form className="price-range-form" onSubmit={handlePriceSubmit}>
              <div className="price-inputs">
                <div className="inputs-container">
                  <input
                    type="number"
                    id="min-price"
                    name="min_price"
                    placeholder={t("filters.min")}
                    min="0"
                    defaultValue={activeFilters.minPrice || ""}
                  />
                  <span>{t("filters.to")}</span>
                  <input
                    type="number"
                    id="max-price"
                    name="max_price"
                    placeholder={t("filters.max")}
                    min="0"
                    defaultValue={activeFilters.maxPrice || ""}
                  />
                </div>
                <div className="price-filter-actions">
                  <button type="submit" className="filter-btn">
                    {t("filters.apply")}
                  </button>
                  <button
                    type="button"
                    className="clear-filter-btn"
                    onClick={handleClearPriceFilter}
                  >
                    {t("filters.clear")}
                  </button>
                </div>
              </div>
            </form>
          )}
        </div>

        {/* On Sale Filter Section */}
        <div className="filter-section">
          {isLoading ? (
            <Skeleton
              width={90}
              height={20}
              className="filter-section-header-skeleton"
            />
          ) : (
            <h3>{t("filters.saleItems")}</h3>
          )}
          {isLoading ? (
            renderOnSaleCheckboxSkeleton()
          ) : (
            <div className="on-sale-filter">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  checked={activeFilters.onSale}
                  onChange={(e) => onSaleToggle(e.target.checked)}
                />
                <span className="checkbox-label">
                  {t("filters.onSaleOnly")}
                </span>
              </label>
            </div>
          )}
        </div>

        {/* Search Filter Section */}
        <div className="filter-section">
          {isLoading ? (
            <Skeleton
              width={130}
              height={20}
              className="filter-section-header-skeleton"
            />
          ) : (
            <h3>{t("filters.searchProducts")}</h3>
          )}
          {isLoading ? (
            renderSearchSkeleton()
          ) : (
            <form className="search-form" onSubmit={handleSearchSubmit}>
              <div className="search-wrapper">
                <input
                  type="text"
                  placeholder={t("filters.searchPlaceholder")}
                  value={searchValue}
                  onChange={handleSearchChange}
                  className="search-input"
                />
                <div className="search-actions">
                  <button type="submit" className="search-btn">
                    {t("common.search")}
                  </button>
                  {searchValue && (
                    <button
                      type="button"
                      className="clear-search-btn"
                      onClick={handleClearSearch}
                    >
                      {t("filters.clear")}
                    </button>
                  )}
                </div>
              </div>
            </form>
          )}
        </div>
      </div>
    </aside>
  );
};

export default FilterSidebar;
